import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Edit } from '@mui/icons-material'
import { Avatar, Box, Card, Chip, IconButton, TableContainer, Tooltip, Typography } from '@mui/material'

import { GRTable, GRTableColumn } from '../../../components/GRTable/GRTable'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { Analyst } from '../../../types/Analyst'
import { useGetIntercomAdminsQuery, useGetInternalAnalystsQuery, useSaveAnalystMutation } from '../../api/analysts'
import { InternalListToolbar } from '../../components/InternalListToolbar/InternalListToolbar'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { AnalystEditDialog } from '../../features/analysts/components/AnalystEditDialog/AnalystEditDialog'
import { analystFieldLabelKeys, getUnstoredAnalystFields, sortAnalysts } from '../../features/analysts/helpers/analystHelpers'
import { AnalystMutation } from '../../features/analysts/types/AnalystMutation'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

type CustomProps = { onEdit: (analyst: Analyst) => void }

type DialogState = { open: boolean; analyst?: Analyst }

export const AnalystsPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-analysts'))
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const analystsQuery = useGetInternalAnalystsQuery()
  const intercomAdminsQuery = useGetIntercomAdminsQuery()
  const [saveAnalyst, { isLoading: isSaving }] = useSaveAnalystMutation()
  const [dialogState, setDialogState] = useState<DialogState>({ open: false })

  const rows = useMemo(() => sortAnalysts(analystsQuery.data ?? []), [analystsQuery.data])

  const tableColumns: GRTableColumn<Analyst, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-analysts:analysts_picture'),
        accessor: ({ row }) => <Avatar alt={row.name} src={row.picture || undefined} sx={{ opacity: row.inactive ? 0.4 : 1 }} />,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { width: 90, minWidth: 90 } },
      },
      {
        labelAccessor: t('internal-analysts:analysts_name'),
        accessor: ({ row }) => (
          <Typography fontWeight="bold" color={row.inactive ? 'text.secondary' : 'text.primary'}>
            {row.name || '---'}
          </Typography>
        ),
        sortable: true,
        sortAccessor: ({ row }) => row.name?.toLowerCase() ?? '',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 200 } },
      },
      {
        labelAccessor: t('internal-analysts:analysts_title'),
        accessor: ({ row }) => row.title,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 220 } },
      },
      {
        labelAccessor: t('internal-analysts:analysts_intercom_id'),
        accessor: ({ row }) => row.intercomId || '---',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 140 } },
      },
      {
        labelAccessor: t('internal-analysts:analysts_status'),
        accessor: ({ row }) =>
          row.inactive ? (
            <Chip size="small" label={t('internal-analysts:analysts_inactive')} />
          ) : (
            <Chip size="small" color="success" label={t('internal-analysts:analysts_active')} />
          ),
        sortable: true,
        sortAccessor: ({ row }) => (row.inactive ? 1 : 0),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 120 } },
      },
      {
        labelAccessor: '',
        accessor: ({ row, customTableProps }) => (
          <Tooltip title={t('internal-analysts:analysts_edit')}>
            <IconButton color="primary" aria-label={t('internal-analysts:analysts_edit')} onClick={() => customTableProps?.onEdit(row)}>
              <Edit />
            </IconButton>
          </Tooltip>
        ),
        cellProps: { align: 'right' },
        headerCellProps: { sx: { width: 80, minWidth: 80 } },
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(() => ({ onEdit: (analyst) => setDialogState({ open: true, analyst }) }), [])

  const handleSave = useCallback(
    async (analyst: AnalystMutation) => {
      const saved = await run(saveAnalyst(analyst).unwrap(), { error: t('internal-analysts:analysts_save_failed') })
      if (!saved.ok) return
      const unstored = getUnstoredAnalystFields(analyst, saved.result)
      if (unstored.length) {
        const fields = unstored.map((field) => t(analystFieldLabelKeys[field])).join(', ')
        notifyError(t('internal-analysts:analysts_not_stored', { fields }))
        return
      }
      notifySuccess(t('internal-analysts:analysts_saved', { name: analyst.name }))
      setDialogState({ open: false })
    },
    [notifyError, notifySuccess, run, saveAnalyst, t]
  )

  return (
    <Box>
      <InternalListToolbar add={{ label: t('internal-analysts:analysts_add'), onClick: () => setDialogState({ open: true }) }} />

      <QueryStateBoundary isLoading={analystsQuery.isLoading} isError={analystsQuery.isError} errorText={t('internal-analysts:analysts_load_failed')}>
        <TableContainer component={Card}>
          <GRTable
            rows={rows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey="id"
            customProps={customProps}
            noRowsLabel={t('internal-common:no_rows')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      <AnalystEditDialog
        open={dialogState.open}
        analyst={dialogState.analyst}
        intercomAdmins={intercomAdminsQuery.isError ? undefined : intercomAdminsQuery.data}
        isLoadingIntercomAdmins={intercomAdminsQuery.isLoading}
        isSaving={isSaving}
        onClose={() => setDialogState({ open: false })}
        onSave={handleSave}
      />
    </Box>
  )
}
